import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { CheckCircle2, PackageCheck, ScanLine, Trash2, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Modal, touch } from './Modal';
import { OrderHistoryDialog } from './OrderHistoryDialog';
import { useScanInput } from '../../hooks/useScanInput';
import { useAudioFeedback } from '../../hooks/useAudioFeedback';
import {
  apiMessage,
  fmtDay,
  receivingApi,
  type Bin,
  type BinLine,
  type BinsBoard as Board,
} from '../../lib/receiving';
import { cn } from '../../utils/cn';

const RECEIVE_ROLES = ['WAREHOUSE_KEEPER', 'POLYVALENT', 'MANAGER', 'OWNER'];
const CANCEL_ROLES = ['COUNTER_AGENT', 'POLYVALENT', 'MANAGER', 'OWNER'];
const norm = (s: string | null | undefined) => (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * The preparation zone (spec §6): fixed slots P-01…P-40. A garage has one bin for all its
 * orders; a consumer one bin per order. There is no free "Prepared" or "Handed over" button: a
 * bin is closed as prepared once its in-stock parts are scanned in, and it is handed over by the
 * exit scan of each part after the collector is identified.
 */
export function BinsBoard({ role, onChanged }: { role: string; onChanged?: () => void }) {
  const { t } = useTranslation();
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [handOver, setHandOver] = useState<Bin | null>(null);
  const [cancelOrder, setCancelOrder] = useState<{ orderId: string; orderNumber: string } | null>(
    null
  );
  const [cancelLine, setCancelLine] = useState<BinLine | null>(null);
  const [history, setHistory] = useState<{ id: string; number: string } | null>(null);

  const load = useCallback(() => {
    receivingApi
      .bins()
      .then(setBoard)
      .catch((err) => setError(apiMessage(err)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const refresh = () => {
    load();
    onChanged?.();
  };

  const prepare = async (slot: string) => {
    try {
      await receivingApi.prepare(slot);
      setMessage({ tone: 'ok', text: t('rcv.bins.prepared', { slot }) });
      refresh();
    } catch (err) {
      setMessage({ tone: 'err', text: apiMessage(err) });
    }
  };

  if (error) return <p className="text-lg text-destructive">{error}</p>;
  if (!board) return <p className="text-lg text-muted-foreground">{t('rcv.loading')}</p>;
  const busy = new Map(board.bins.map((b) => [b.slot, b]));

  return (
    <div className="space-y-4">
      <p className="text-base text-muted-foreground">{t('rcv.bins.intro')}</p>
      {message && (
        <p
          className={cn(
            'rounded-lg px-4 py-3 text-base',
            message.tone === 'ok'
              ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
              : 'bg-destructive/10 text-destructive'
          )}
          role="status"
        >
          {message.text}
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {board.slots.map((slot) => {
          const b = busy.get(slot);
          if (!b) {
            return (
              <div
                key={slot}
                className="flex items-center gap-3 rounded-xl border border-dashed px-4 py-3 text-muted-foreground"
              >
                <span className="text-2xl font-bold">{slot}</span> <span>{t('rcv.bins.free')}</span>
              </div>
            );
          }
          return (
            <div
              key={slot}
              className={cn(
                'rounded-xl border-2 bg-card p-4',
                b.status === 'COMPLETE' && 'border-emerald-600/70',
                b.status === 'PREPARED' && 'border-sky-600/70'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-4xl font-black leading-none">{slot}</div>
                <span
                  className={cn(
                    'rounded-md px-2 py-1 text-sm font-semibold',
                    b.status === 'OPEN' && 'bg-muted',
                    b.status === 'COMPLETE' && 'bg-emerald-600 text-white',
                    b.status === 'PREPARED' && 'bg-sky-600 text-white'
                  )}
                >
                  {t(`rcv.bins.status.${b.status}`)}
                </span>
              </div>
              <div className="mt-2 text-lg font-semibold">
                {b.customerName ?? t('rcv.walkIn')}{' '}
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                  {b.customerType === 'B2B' ? t('rcv.garage') : t('rcv.consumer')}
                </span>
              </div>
              <div className="text-base">
                {t('rcv.card.binProgress', { n: b.unitsIn, of: b.unitsDue, count: b.ordersDue })}
              </div>
              <ul className="mt-2 space-y-1 text-sm">
                {b.orders.map((o) => (
                  <li key={o.orderId} className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="font-semibold underline-offset-2 hover:underline"
                      onClick={() => setHistory({ id: o.orderId, number: o.orderNumber })}
                    >
                      {o.orderNumber}
                    </button>
                    <span className="text-muted-foreground">
                      {t('rcv.by', { name: o.orderedBy })}
                    </span>
                    {!o.inBin && (
                      <span className="text-muted-foreground">({t('rcv.bins.nothingYet')})</span>
                    )}
                    {CANCEL_ROLES.includes(role) && (
                      <button
                        type="button"
                        className="ml-auto inline-flex min-h-10 items-center gap-1 rounded px-2 text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          setCancelOrder({ orderId: o.orderId, orderNumber: o.orderNumber })
                        }
                      >
                        <Trash2 className="h-4 w-4" /> {t('rcv.bins.cancelOrder')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {b.waitingToday.length > 0 && (
                <LineList
                  title={t('rcv.bins.notArrived')}
                  lines={b.waitingToday}
                  action={CANCEL_ROLES.includes(role) ? (l) => setCancelLine(l) : undefined}
                  actionLabel={t('rcv.bins.cancelUnits')}
                />
              )}
              {b.expectedLater.length > 0 && (
                <LineList
                  title={t('rcv.bins.expectedLater')}
                  lines={b.expectedLater}
                  action={CANCEL_ROLES.includes(role) ? (l) => setCancelLine(l) : undefined}
                  actionLabel={t('rcv.bins.cancelUnits')}
                />
              )}
              {b.inStockToPick.length > 0 && (
                <LineList title={t('rcv.bins.inStockToPick')} lines={b.inStockToPick} shelf />
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {RECEIVE_ROLES.includes(role) &&
                  b.status === 'COMPLETE' &&
                  b.inStockToPick.length === 0 && (
                    <Button variant="outline" className={touch} onClick={() => void prepare(slot)}>
                      <PackageCheck className="h-5 w-5" /> {t('rcv.bins.closePrepared')}
                    </Button>
                  )}
                <Button
                  className={touch}
                  onClick={() => setHandOver(b)}
                  disabled={b.unitsIn === 0 && b.inBin.length === 0}
                >
                  <ScanLine className="h-5 w-5" /> {t('rcv.bins.handOver')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <HandOverDialog
        bin={handOver}
        role={role}
        onClose={() => setHandOver(null)}
        onDone={(text) => {
          setHandOver(null);
          setMessage({ tone: 'ok', text });
          refresh();
        }}
      />
      <CancelOrderDialog
        target={cancelOrder}
        onClose={() => setCancelOrder(null)}
        onDone={(text) => {
          setCancelOrder(null);
          setMessage({ tone: 'ok', text });
          refresh();
        }}
      />
      <CancelLineDialog
        line={cancelLine}
        onClose={() => setCancelLine(null)}
        onDone={(text) => {
          setCancelLine(null);
          setMessage({ tone: 'ok', text });
          refresh();
        }}
      />
      <OrderHistoryDialog
        orderId={history?.id ?? null}
        orderNumber={history?.number ?? null}
        onClose={() => setHistory(null)}
      />
    </div>
  );
}

function LineList({
  title,
  lines,
  shelf = false,
  action,
  actionLabel,
}: {
  title: string;
  lines: BinLine[];
  shelf?: boolean;
  action?: (l: BinLine) => void;
  actionLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-2">
      <div className="text-sm font-semibold text-muted-foreground">{title}</div>
      <ul className="text-sm">
        {lines.map((l) => (
          <li key={l.lineId} className="flex flex-wrap items-center gap-x-2">
            <span>
              {l.qty - (l.source === 'EXTERNAL' ? l.received : l.picked)} × {l.productName}
            </span>
            <span className="text-muted-foreground">
              ({l.orderNumber}
              {shelf
                ? ` · ${l.shelfLocation ?? t('rcv.card.noLocation')}`
                : ` · ${fmtDay(l.promisedAt)}`}
              )
            </span>
            {action && (
              <button
                type="button"
                className="ml-auto min-h-10 rounded px-2 text-destructive hover:bg-destructive/10"
                onClick={() => action(l)}
              >
                {actionLabel}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Hand-over: identify the collector, then scan each part out of the bin. A part that is not in
 * the bin is declared "not found" (Zone Q, anomaly) and the customer leaves with the rest.
 */
function HandOverDialog({
  bin,
  role,
  onClose,
  onDone,
}: {
  bin: Bin | null;
  role: string;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { t } = useTranslation();
  const audio = useAudioFeedback();
  const [collector, setCollector] = useState('');
  const [pickupCode, setPickupCode] = useState('');
  const [idName, setIdName] = useState('');
  const [scans, setScans] = useState<Array<{ code: string; qty: number }>>([]);
  const [notFound, setNotFound] = useState<Record<string, string>>({});
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const field = useRef<HTMLInputElement>(null);

  /* WHAT leaves (owner's decision, 2026-10-23): the delivery note covers orders, not the bin.
     Ready orders are picked by default; an incomplete order stays in the bin unless the employee
     picks it, or ticks some of its lines because the garage wants them now (the rest follows on
     a second delivery note). */
  const present = (l: BinLine) =>
    Math.max(
      0,
      (l.source === 'EXTERNAL' ? l.received : l.status === 'PICKED' ? l.picked : 0) - l.delivered
    );
  const binOrders = useMemo(() => {
    const lines = bin?.lines ?? [];
    return (bin?.orders ?? [])
      .filter((o) => o.inBin)
      .map((o) => {
        const ls = lines.filter((l) => l.orderId === o.orderId);
        const here = ls.filter((l) => present(l) > 0);
        const ready = ls.length > 0 && ls.every((l) => present(l) >= l.qty - l.delivered);
        return { ...o, lines: ls, here, ready };
      })
      .filter((o) => o.here.length > 0);
  }, [bin]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pickedOrders, setPickedOrders] = useState<Set<string>>(new Set());
  const [pickedLines, setPickedLines] = useState<Set<string>>(new Set());
  useEffect(() => {
    setPickedOrders(new Set(binOrders.filter((o) => o.ready).map((o) => o.orderId)));
    setPickedLines(new Set());
  }, [binOrders]);
  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  useEffect(() => {
    setCollector('');
    setPickupCode('');
    setIdName('');
    setScans([]);
    setNotFound({});
    setError('');
    setTyped('');
  }, [bin?.id]);

  const toHand = useMemo(
    () =>
      (bin?.lines ?? []).filter(
        (l) =>
          (l.source === 'EXTERNAL' ? l.received : l.status === 'PICKED' ? l.picked : 0) >
            l.delivered &&
          (pickedOrders.has(l.orderId) || pickedLines.has(l.lineId))
      ),
    [bin, pickedOrders, pickedLines]
  );

  /** How many units of each line the scans so far account for (oldest order first). */
  const covered = useMemo(() => {
    const left = new Map<string, number>();
    const out = new Map<string, number>();
    for (const s of scans) left.set(norm(s.code), (left.get(norm(s.code)) ?? 0) + s.qty);
    for (const l of toHand) {
      const key = [norm(l.ean), norm(l.ref)].find((k) => k && (left.get(k) ?? 0) > 0);
      if (!key) continue;
      const inBin = (l.source === 'EXTERNAL' ? l.received : l.picked) - l.delivered;
      const n = Math.min(inBin, left.get(key) ?? 0);
      out.set(l.lineId, n);
      left.set(key, (left.get(key) ?? 0) - n);
    }
    return out;
  }, [scans, toHand]);

  const addScan = useCallback(
    (code: string) => {
      const c = code.trim();
      if (!c) return;
      const known = toHand.some((l) => norm(l.ean) === norm(c) || norm(l.ref) === norm(c));
      if (known) audio.success();
      else audio.error();
      setScans((s) => [...s, { code: c, qty: 1 }]);
    },
    [toHand, audio]
  );

  useScanInput({ onScan: addScan, paused: !bin });

  if (!bin) return null;
  const locked = bin.lines.find((l) => l.pickupLockedAt);
  const remaining = toHand.filter(
    (l) =>
      !notFound[l.lineId] &&
      (covered.get(l.lineId) ?? 0) < (l.source === 'EXTERNAL' ? l.received : l.picked) - l.delivered
  );

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await receivingApi.handOver(bin.slot, {
        collectorName: collector || undefined,
        pickupCode: pickupCode || undefined,
        idCheckedName: idName || undefined,
        scans,
        notFound: Object.entries(notFound)
          .filter(([lineId]) => toHand.some((l) => l.lineId === lineId))
          .map(([lineId, reason]) => ({ lineId, reason })),
        orderIds: [...pickedOrders],
        lineIds: [...pickedLines].filter(
          (id) => !pickedOrders.has(bin.lines.find((l) => l.lineId === id)?.orderId ?? '')
        ),
      });
      // 2026-10-08 — one hand-over, one document, by client type: the particulier's ticket,
      // the pro comptant's invoice(s), else (on account) the priced BL. The driver's priceless
      // copy stays printable from the order.
      const open = (path: string) =>
        apiClient
          .get(path, { responseType: 'blob' })
          .then((res) => {
            const url = URL.createObjectURL(res.data as Blob);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
          })
          .catch(() => undefined);
      const docs = (r.orders ?? []).flatMap((o) =>
        o.receipt
          ? [`/receipts/${o.receipt.id}/pdf`]
          : o.invoice
            ? [`/invoices/${o.invoice.id}/order-pdf`]
            : []
      );
      if (docs.length) docs.forEach((d) => void open(d));
      else if (r.deliveryNote?.id) void open(`/delivery-notes/${r.deliveryNote.id}/client-pdf`);
      onDone(
        t('rcv.bins.handedOver', {
          slot: bin.slot,
          note: r.deliveryNote?.deliveryNoteNumber ?? r.deliveryNote?.number ?? '',
          still: r.stillExpected,
        })
      );
    } catch (err) {
      setError(apiMessage(err));
      audio.error();
    } finally {
      setBusy(false);
    }
  };
  const unlock = async () => {
    if (!locked) return;
    try {
      await receivingApi.unlockPickup(locked.orderId, unlockReason);
      setError(t('rcv.bins.unlocked'));
    } catch (err) {
      setError(apiMessage(err));
    }
  };

  return (
    <Modal
      open
      wide
      title={t('rcv.bins.handOverTitle', { slot: bin.slot })}
      subtitle={`${bin.customerName ?? ''} · ${bin.orders.map((o) => o.orderNumber).join(', ')}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className={touch} onClick={onClose}>
            {t('rcv.cancel')}
          </Button>
          <Button
            className={touch}
            disabled={busy || toHand.length === 0 || scans.length === 0 || remaining.length > 0}
            onClick={() => void submit()}
          >
            <CheckCircle2 className="h-5 w-5" /> {t('rcv.bins.confirmHandOver')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <section>
          <h3 className="mb-2 text-lg font-semibold">{t('rcv.bins.step1')}</h3>
          {bin.customerType === 'B2B' ? (
            /* Driver delivery v1: whoever collects for the garage (our driver, theirs, a courier)
               gives the PIN of each order — the authorisation — and their first and last name,
               printed on the unpriced delivery note they leave with. */
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="gpin" className="text-base">
                  {t('rcv.bins.garagePins', {
                    orders: bin.orders.map((o) => o.orderNumber).join(', '),
                  })}
                </Label>
                <Input
                  id="gpin"
                  inputMode="numeric"
                  className="h-12 text-2xl tracking-[0.3em]"
                  value={pickupCode}
                  onChange={(e) => setPickupCode(e.target.value.replace(/[^\d ]/g, ''))}
                  placeholder="0000 0000"
                />
              </div>
              <div>
                <Label htmlFor="collector" className="text-base">
                  {t('rcv.bins.collector')}
                </Label>
                <Input
                  id="collector"
                  className="h-12 text-lg"
                  value={collector}
                  onChange={(e) => setCollector(e.target.value)}
                  placeholder={t('rcv.bins.collectorHint')}
                />
              </div>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="pcode" className="text-base">
                  {t('rcv.bins.pickupCode')}
                </Label>
                <Input
                  id="pcode"
                  inputMode="numeric"
                  maxLength={4}
                  className="h-12 text-2xl tracking-[0.5em]"
                  value={pickupCode}
                  onChange={(e) => setPickupCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <Label htmlFor="idname" className="text-base">
                  {t('rcv.bins.idName')}
                </Label>
                <Input
                  id="idname"
                  className="h-12 text-lg"
                  value={idName}
                  onChange={(e) => setIdName(e.target.value)}
                />
              </div>
            </div>
          )}
          {locked && (
            <div className="mt-3 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-base">
              <p className="font-semibold text-destructive">{t('rcv.bins.locked')}</p>
              {['MANAGER', 'OWNER'].includes(role) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Input
                    className="h-12 flex-1 text-base"
                    placeholder={t('rcv.reason10')}
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    className={touch}
                    disabled={unlockReason.trim().length < 10}
                    onClick={() => void unlock()}
                  >
                    {t('rcv.bins.unlock')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-lg font-semibold">{t('rcv.bins.stepOrders')}</h3>
          <ul className="divide-y rounded-lg border">
            {binOrders.map((o) => (
              <li key={o.orderId} className="px-3 py-2 text-base">
                <label className="flex min-h-10 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={pickedOrders.has(o.orderId)}
                    onChange={() => setPickedOrders((s) => toggle(s, o.orderId))}
                  />
                  <span className="font-semibold">{o.orderNumber}</span>
                  <span
                    className={cn(
                      'rounded px-2 py-0.5 text-sm',
                      o.ready
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                    )}
                  >
                    {o.ready
                      ? t('rcv.bins.orderReady')
                      : t('rcv.bins.orderIncomplete', {
                          here: o.here.length,
                          total: o.lines.length,
                        })}
                  </span>
                </label>
                {/* An incomplete order not picked whole: the garage may still take what is here. */}
                {!o.ready && !pickedOrders.has(o.orderId) && (
                  <ul className="ml-8 mt-1 space-y-1">
                    {o.here.map((l) => (
                      <li key={l.lineId}>
                        <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={pickedLines.has(l.lineId)}
                            onChange={() => setPickedLines((s) => toggle(s, l.lineId))}
                          />
                          {t('rcv.bins.takeLineAnyway', { name: l.productName, ref: l.ref ?? '' })}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-sm text-muted-foreground">{t('rcv.bins.ordersHint')}</p>
        </section>
        <section>
          <h3 className="mb-2 text-lg font-semibold">{t('rcv.bins.step2')}</h3>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              addScan(typed);
              setTyped('');
              field.current?.focus();
            }}
          >
            <Input
              ref={field}
              className="h-12 text-lg"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={t('rcv.bins.exitScanHint')}
            />
            <Button type="submit" variant="outline" className={touch}>
              {t('rcv.add')}
            </Button>
          </form>
          <ul className="mt-3 divide-y rounded-lg border">
            {toHand.map((l) => {
              const inBin = (l.source === 'EXTERNAL' ? l.received : l.picked) - l.delivered;
              const got = covered.get(l.lineId) ?? 0;
              const nf = notFound[l.lineId];
              return (
                <li
                  key={l.lineId}
                  className="flex flex-wrap items-center gap-3 px-3 py-2 text-base"
                >
                  {got >= inBin ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : nf ? (
                    <XCircle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <ScanLine className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="font-semibold">
                    {got}/{inBin}
                  </span>
                  <span className="min-w-0 flex-1">
                    {l.productName}{' '}
                    <span className="text-muted-foreground">
                      · {l.ref} · {l.orderNumber}
                    </span>
                  </span>
                  {got < inBin &&
                    (nf !== undefined ? (
                      <Input
                        className="h-10 w-64 text-sm"
                        placeholder={t('rcv.bins.notFoundReason')}
                        value={nf}
                        onChange={(e) => setNotFound({ ...notFound, [l.lineId]: e.target.value })}
                      />
                    ) : (
                      <button
                        type="button"
                        className="min-h-10 rounded px-2 text-sm text-amber-700 hover:bg-amber-500/10 dark:text-amber-400"
                        onClick={() => setNotFound({ ...notFound, [l.lineId]: '' })}
                      >
                        {t('rcv.bins.notInBin')}
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
          {remaining.length > 0 && scans.length > 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              {t('rcv.bins.remaining', { count: remaining.length })}
            </p>
          )}
        </section>
        {error && (
          <p
            className="rounded-lg bg-destructive/10 px-4 py-3 text-base text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function CancelOrderDialog({
  target,
  onClose,
  onDone,
}: {
  target: { orderId: string; orderNumber: string } | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setReason('');
    setError('');
  }, [target?.orderId]);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await receivingApi.cancelOrder(target.orderId, reason);
      onDone(
        t('rcv.bins.orderCancelled', {
          number: target.orderNumber,
          back:
            r.putBack.map((p) => `${p.qty} × ${p.name} → ${p.location ?? '—'}`).join(', ') ||
            t('rcv.bins.nothingToPutBack'),
        })
      );
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      title={t('rcv.bins.cancelOrderTitle', { number: target.orderNumber })}
      subtitle={t('rcv.bins.cancelOrderHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className={touch} onClick={onClose}>
            {t('rcv.cancel')}
          </Button>
          <Button
            variant="destructive"
            className={touch}
            disabled={busy || reason.trim().length < 10}
            onClick={() => void submit()}
          >
            {t('rcv.bins.cancelOrder')}
          </Button>
        </>
      }
    >
      <Label htmlFor="cr" className="text-base">
        {t('rcv.reason10')}
      </Label>
      <Textarea
        id="cr"
        className="text-base"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {error && (
        <p className="mt-2 text-destructive" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/** "3 pads ordered, 2 arrived, the customer cancels the 3rd" (spec §9). */
export function CancelLineDialog({
  line,
  onClose,
  onDone,
}: {
  line:
    | BinLine
    | { lineId: string; productName: string; orderNumber: string; notYet: number }
    | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { t } = useTranslation();
  const max = line ? ('notYet' in line ? line.notYet : line.qty - line.received) : 0;
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setQty(Math.max(1, max));
    setReason('');
    setConsent(false);
    setError('');
  }, [line?.lineId, max]);
  if (!line) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await receivingApi.cancelLine(line.lineId, { qty, reason, consent });
      onDone(
        [r.message, ...r.effects, ...r.docs.map((d) => `${d.number} · ${d.text}`)].join(' — ')
      );
    } catch (err) {
      setError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      title={t('rcv.cancelLine.title', { name: line.productName, number: line.orderNumber })}
      subtitle={t('rcv.cancelLine.hint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className={touch} onClick={onClose}>
            {t('rcv.cancel')}
          </Button>
          <Button
            variant="destructive"
            className={touch}
            disabled={busy || !consent || reason.trim().length < 10 || qty < 1 || qty > max}
            onClick={() => void submit()}
          >
            {t('rcv.cancelLine.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="cq" className="text-base">
            {t('rcv.cancelLine.qty', { max })}
          </Label>
          <Input
            id="cq"
            type="number"
            min={1}
            max={max}
            className="h-12 w-32 text-lg"
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
          />
        </div>
        <div>
          <Label htmlFor="clr" className="text-base">
            {t('rcv.reason10')}
          </Label>
          <Textarea
            id="clr"
            rows={2}
            className="text-base"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <label className="flex min-h-12 items-center gap-3 text-base">
          <input
            type="checkbox"
            className="h-6 w-6"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          {t('rcv.consent')}
        </label>
        {error && (
          <p className="text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
